// Reusable manual framing system (used across the whole site).
// A "frame" describes how an image is positioned inside its (fixed) container,
// WITHOUT ever modifying the original file — everything is pure CSS.
//   { zoom: 1..3, posX: 0..100, posY: 0..100, fit: "cover" | "contain" }
// An empty/undefined frame renders exactly like the default (centered cover).
export const frameStyle = (frame, defaultFit = "cover") => {
  const f = frame || {};
  const px = f.posX ?? 50;
  const py = f.posY ?? 50;
  const z = f.zoom ?? 1;
  return {
    objectFit: f.fit || defaultFit,
    objectPosition: `${px}% ${py}%`,
    transform: `scale(${z})`,
    // Anchor the zoom on the chosen focal point so X/Y stay coherent and the
    // pan amplitude grows with zoom (classic transform-tool behaviour).
    transformOrigin: `${px}% ${py}%`,
  };
};

// Frame display format (shape of the container). "auto"/unset keeps the section default.
// "original" = le conteneur ÉPOUSE les proportions intrinsèques du média (aucun recadrage imposé).
export const FORMAT_ASPECT = {
  horizontal: "aspect-video",
  vertical: "aspect-[2/3]",
  square: "aspect-square",
};
export const frameAspectClass = (frame, fallback = "aspect-video") => {
  const fmt = frame && frame.format;
  if (fmt === "original") return null; // ratio intrinsèque (détecté au chargement)
  return (fmt && FORMAT_ASPECT[fmt]) || fallback;
};

// Options d'affichage du BLOC conteneur (stockées dans le même objet frame, par média) :
//   fit: "cover" (remplit, peut recadrer) | "contain" (proportions conservées, jamais recadré)
//   noBg: true → fond du conteneur masqué · border: true → bordure fine visible
//   maxW: "full" | "large" | "medium" → largeur maximale du bloc
export const MAXW_CLASS = { full: "", large: "max-w-5xl", medium: "max-w-3xl" };
export const frameBlockClass = (frame) => {
  const f = frame || {};
  return [
    "overflow-hidden mx-auto w-full",
    f.border ? "border border-white/15 rounded-lg" : "",
    f.noBg ? "" : "bg-[#0e0e0e]",
    MAXW_CLASS[f.maxW] || "",
  ].filter(Boolean).join(" ");
};

// True when the frame differs from the default (used to keep things lightweight).
export const hasFrame = (f) =>
  !!f && ((f.zoom && f.zoom !== 1) || (f.posX != null && f.posX !== 50) || (f.posY != null && f.posY !== 50) || f.fit === "contain" || f.format === "original" || !!f.noBg || !!f.border || (f.maxW && f.maxW !== "full"));

// ---- Subtle legibility effects (help images stand out from the dark background) ----
// effects = { shadow: 0|1|2, ring: bool, halo: bool, brightness: 100 }
// Kept intentionally discreet — never alters the site's visual identity.
const SHADOWS = { 1: "0 8px 24px rgba(0,0,0,0.45)", 2: "0 14px 40px rgba(0,0,0,0.65)" };

export const imgFilter = (effects) => {
  const e = effects || {};
  return e.brightness != null && e.brightness !== 100 ? `brightness(${e.brightness}%)` : undefined;
};

export const boxEffectStyle = (effects) => {
  const e = effects || {};
  const parts = [];
  if (e.ring) parts.push("0 0 0 1px rgba(255,255,255,0.18)");
  if (e.shadow) parts.push(SHADOWS[e.shadow] || SHADOWS[1]);
  if (e.halo) parts.push("0 0 36px rgba(90,150,255,0.22)");
  return parts.length ? { boxShadow: parts.join(", ") } : undefined;
};

export const hasEffects = (e) =>
  !!e && (e.shadow || e.ring || e.halo || (e.brightness != null && e.brightness !== 100));

